'use client';
import { jsx as _jsx } from "react/jsx-runtime";
import { SafeLocalStorage } from '#pplx/frontend/browser-storage/localStorage.js';
import { _withRetry } from '#pplx/frontend/dynamic/DynamicShared.js';
import { logger } from '#pplx/frontend/logger/browser.js';
import React, { useEffect, useRef, useState } from 'react';
const EMPTY_OBJECT = {};
const KEY = 'lastCriticalImportFailure';
const THRESHOLD_TIME = 1000 * 15;
const DYNAMIC_RESTRICTED_IMPORT_FAILURE_EVENT = 'pplx:dynamic-restricted-import-failure';
const EmptyComponent = () => null;
// Type-erased cache to avoid TypeScript performance issues with deeply nested generics
// Using `any` for the key type avoids expensive structural type comparisons across
// different instantiations of Dynamic<TProps>. The cache is internal and type-safe
// because we only retrieve components using their original import function.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const imports = new Map();
/**
 * Extract the path from the import function
 * @param importFn - The import function
 * @private Exported for testing
 * @returns The path or null if the path cannot be extracted
 */
/**
 * Add a timestamp to the path to aid in cache busting
 * @param path - The path to add the timestamp to
 * @private Exported for testing
 * @returns The path with the timestamp
 */
/**
 * Retry the import function up to MAX_RETRIES times
 * @param importFn - The import function
 * @returns The result of the import function
 */
/**
 * Create a dynamic component that only imports the code when it is mounted
 * @param importFn - The import function
 * @param options - The options for the dynamic component
 * @returns The dynamic component
 */
function _Dynamic(importFn, options = EMPTY_OBJECT) {
    const LoadingComponent = options.loading ?? EmptyComponent;
    const ErrorComponent = options.error;
    let pendingImport;
    let hasImportError = false;
    const doImport = () => {
        if (imports.has(importFn)) {
            return Promise.resolve();
        }
        if (pendingImport) {
            return pendingImport;
        }
        pendingImport = (async () => {
            try {
                const mod = await (options.noRetry ? importFn() : _withRetry(importFn));
                imports.set(importFn, mod.default);
                hasImportError = false;
            }
            catch (err) {
                hasImportError = true;
                if (options.restricted || options.restrictedFeatureLoader) {
                    window.dispatchEvent(new CustomEvent(DYNAMIC_RESTRICTED_IMPORT_FAILURE_EVENT, {
                        detail: {
                            error: err,
                            options,
                        },
                    }));
                    if (options.restricted)
                        logger.debug(err);
                }
                else {
                    logger.error(err);
                }
                if (options.critical) {
                    if (_isCriticalImportFailureTooRecent()) {
                        return;
                    }
                    else if (_setLastCriticalImportFailure()) {
                        logger.warn('Reloading window', {
                            reason: 'critical import failure',
                            url: window.location.pathname,
                        });
                        window.location.reload();
                    }
                }
            }
            finally {
                pendingImport = undefined;
            }
        })();
        return pendingImport;
    };
    const TheDynamicComponent = function TheDynamicComponent(props) {
        const [, rerender] = useState(0);
        const hasComponentRef = useRef(false);
        useEffect(() => {
            // React lifecyle is aware of component and has rendered it
            if (hasComponentRef.current) {
                return;
            }
            // React lifecycle is unaware, but it's imported and just needs to be rendered
            if (imports.has(importFn)) {
                if (options.nextFrame) {
                    setTimeout(() => rerender((r) => r + 1));
                }
                else {
                    rerender((r) => r + 1);
                }
                return;
            }
            // React is unaware, it's not imported yet, so we need to import it and render
            doImport().then(() => {
                if (options.nextFrame) {
                    setTimeout(() => rerender((r) => r + 1));
                }
                else {
                    rerender((r) => r + 1);
                }
            });
        }, []);
        const Component = imports.get(importFn);
        hasComponentRef.current = !!Component;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if (Component)
            return _jsx(Component, { ...props });
        if (hasImportError && ErrorComponent) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            return _jsx(ErrorComponent, { children: props.children });
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return _jsx(LoadingComponent, { children: props.children });
    };
    const TheRestrictedDynamicComponent = function TheRestrictedDynamicComponent(props) {
        return (_jsx(RestrictedFeatureLoader, { children: _jsx(TheDynamicComponent, { ...props }) }));
    };
    const DynamicComponent = (options.restricted ? TheRestrictedDynamicComponent : TheDynamicComponent);
    Object.defineProperties(DynamicComponent, {
        __dynamic: { value: true },
        preload: { value: doImport },
    });
    return DynamicComponent;
}
// We need to load this with non-restricted behaviour to avoid an infinite loop
const RestrictedFeatureLoader = _Dynamic(async () => {
    const { RestrictedFeatureLoader } = await import('#pplx/frontend/restricted-feature-loader/index.js');
    return { default: RestrictedFeatureLoader };
}, { noRetry: true, restrictedFeatureLoader: true });
export const Dynamic = _Dynamic;
const _getLastCriticalImportFailure = () => {
    const lastCriticalImportFailure = SafeLocalStorage.getItem(KEY);
    if (!lastCriticalImportFailure)
        return null;
    return new Date(Number.parseInt(lastCriticalImportFailure, 10));
};
const _setLastCriticalImportFailure = () => {
    return SafeLocalStorage.setItem(KEY, Date.now().toString());
};
const _isCriticalImportFailureTooRecent = () => {
    const lastCriticalImportFailure = _getLastCriticalImportFailure();
    if (!lastCriticalImportFailure || isNaN(lastCriticalImportFailure.getTime()))
        return false;
    return lastCriticalImportFailure.getTime() > Date.now() - THRESHOLD_TIME;
};
//# sourceMappingURL=Dynamic.js.map